import type React from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { PieChart as PieChartIcon, Trophy } from "lucide-react"
import { useRaceStatsData } from "./useRaceStatsData"
import { EmptyRacesState } from "./EmptyRacesState"
import { DistributionSummaryCards } from "./DistributionSummaryCards"
import { EditionCharts } from "./EditionCharts"
import { RaceCharts } from "./RaceCharts"
import { RaceDetailTable } from "./RaceDetailTable"
import { PerformanceFilters } from "./PerformanceFilters"
import { PerformanceSummaryCards } from "./PerformanceSummaryCards"
import { PerformanceCharts } from "./PerformanceCharts"
import { RaceMatrixTable } from "./RaceMatrixTable"
import { PerformanceEmptyState } from "./PerformanceEmptyState"
import type { TournamentRaceStatsProps } from "./types"

export const TournamentRaceStats: React.FC<TournamentRaceStatsProps> = ({
  tournament,
  players,
}) => {
  const {
    selectedRoundId,
    setSelectedRoundId,
    availableRounds,
    selectedRound,
    standingLoading,
    selectedTable,
    availableTables,
    toggleTable,
    raceMatrix,
    shouldGroupRaces,
    editionStats,
    raceStats,
    raceChartConfig,
    editionChartConfig,
    racePerformanceStats,
    totalPlayers,
    playersWithRaces,
    playersWithoutRaces,
  } = useRaceStatsData(tournament, players)

  if (!tournament.Game?.RaceGames?.length) {
    return <EmptyRacesState />
  }

  return (
    <Tabs defaultValue="distribution" className="w-full">
      <TabsList className="grid w-full grid-cols-2 bg-gray-100 dark:bg-gray-800 rounded-md">
        <TabsTrigger
          value="distribution"
          className="flex items-center gap-2 text-gray-700 dark:text-gray-200 data-[state=active]:bg-primary data-[state=active]:text-white dark:data-[state=active]:text-white"
        >
          <PieChartIcon className="h-4 w-4" />
          <span className="hidden sm:inline">Distribución</span>
        </TabsTrigger>

        <TabsTrigger
          value="performance"
          className="flex items-center gap-2 text-gray-700 dark:text-gray-200 data-[state=active]:bg-primary data-[state=active]:text-white dark:data-[state=active]:text-white"
        >
          <Trophy className="h-4 w-4" />
          <span className="hidden sm:inline">Rendimiento</span>
        </TabsTrigger>
      </TabsList>

      <TabsContent value="distribution" className="space-y-6 mt-6">
        <DistributionSummaryCards
          totalPlayers={totalPlayers}
          playersWithRaces={playersWithRaces}
          playersWithoutRaces={playersWithoutRaces}
          raceStats={raceStats}
        />

        <div className="space-y-6">
          {shouldGroupRaces && (
            <EditionCharts editionStats={editionStats} editionChartConfig={editionChartConfig} />
          )}

          <RaceCharts raceStats={raceStats} raceChartConfig={raceChartConfig} />
        </div>

        <RaceDetailTable raceStats={raceStats} />
      </TabsContent>

      <TabsContent value="performance" className="space-y-6 mt-6">
        <PerformanceFilters
          availableRounds={availableRounds}
          selectedRoundId={selectedRoundId}
          onSelectedRoundIdChange={setSelectedRoundId}
          availableTables={availableTables}
          selectedTable={selectedTable}
          onToggleTable={toggleTable}
          selectedRoundName={selectedRound?.name}
        />

        {!standingLoading && racePerformanceStats.length > 0 ? (
          <>
            <PerformanceSummaryCards racePerformanceStats={racePerformanceStats} />

            <PerformanceCharts
              racePerformanceStats={racePerformanceStats}
              raceStats={raceStats}
              raceChartConfig={raceChartConfig}
            />

            <RaceMatrixTable raceMatrix={raceMatrix} />
          </>
        ) : (
          <PerformanceEmptyState loading={standingLoading} />
        )}
      </TabsContent>
    </Tabs>
  )
}
